import Cookies from 'js-cookie';
import type * as React from 'react';
import { commonSourceParam } from '../platform/environment';

export function createPromiseObj<T>(): {
  promise: Promise<T>;
  resolve: typeof Promise.resolve;
  reject: typeof Promise.reject;
} {
  let resolve, reject;
  const promise = new Promise<T>((_resolve, _reject) => {
    resolve = _resolve;
    reject = _reject;
  });
  return { promise, resolve, reject };
};

export function animateWithDuration(
  duration: number,
  animation: (progress: number) => void,
  timingFunction?: (t: number) => number,
  completion?: () => void,
) {
  const start = new Date().getTime();
  const intervalId = setInterval(() => {
    const progress = (new Date().getTime() - start) / duration;
    if (progress >= 1) {
      animation(1);
      clearInterval(intervalId);
      completion?.();
    } else {
      animation(timingFunction ? timingFunction(progress) : progress);
    }
  }, 16);
}

export function animationEaseOut(t: number) {
  const pow3 = (x: number) => x * x * x;
  return pow3(t - 1) + 1;
}

// export const supportWebP = process.env.IS_SERVER
//   ? false
//   : (() =>
//       document.createElement('canvas').toDataURL('image/webp').startsWith('data:image/webp'))();

export function getJSONParse(param: any) {
  try {
    if (typeof param !== 'string') {
      return param;
    }
    return JSON.parse(param);
  } catch (e) {
    return param;
  }
}

export const story2issueKeyMap = {
  fields: 'fields',
  tableItems: 'table_items',
  formItems: 'form_items',
  filterItems: 'filter_items',
  groupItems: 'group_items',
  dashboardTableItems: 'dashboard_table_items',
  storyTabItems: 'story_tab_items',
};
export const issue2storyKeyMap = {
  fields: 'fields',
  table_items: 'tableItems',
  form_items: 'formItems',
  filter_items: 'filterItems',
  group_items: 'groupItems',
  dashboard_table_items: 'dashboardTableItems',
  story_tab_items: 'storyTabItems',
};

export function findTargetKeyIndexInArray(
  arr: {
    [key: string]: any;
  }[],
  key: string,
  val: any,
) {
  let index = -1;
  if (arr.length > 0) {
    arr.forEach((item, num) => {
      if (item[key] === val) {
        index = num;
      }
    });
  }
  return index;
}

export const prefixUrl = '';

export const routePlgReg = /^meegoPlg\//;

/**
 * 将阿拉伯数字转换为汉字数字
 * @param digit 待转换数字
 * @param preOpt 可选 优化 “一十x” 这种case
 */
// export function toZhDigit(digit: number, preOpt?: boolean) {
//   if (!isZh) {
//     return digit;
//   }
//   if (!digit) {
//     return '零'; // T_IGNORE
//   }
//   const digitStr = String(digit);
//   if (digitStr.length > 4) {
//     return digit;
//   }
//   const zh = ['零', '一', '二', '三', '四', '五', '六', '七', '八', '九']; // T_IGNORE
//   const unit = ['千', '百', '十', '']; // T_IGNORE

//   let result = '';

//   for (let i = 0; i < digitStr.length; i++) {
//     if (digitStr[i] === '0' && i !== digitStr.length - 1 && digitStr[i + 1] !== '0') {
//       result += zh[digitStr[i]];
//     } else if (digitStr[i] !== '0') {
//       result += zh[digitStr[i]] + unit[4 - digitStr.length + i];
//     }
//   }

//   if (preOpt) {
//     return result.startsWith('一十') ? result.replace('一十', '十') : result; // T_IGNORE
//   }

//   return result;
// }

// 下划线转换驼峰
export function toHump(name: string) {
  return name.replace(/_(\w)/g, (_all, letter) => letter.toUpperCase());
}

// 驼峰转换下划线
export function toLine(name: string) {
  return name.replace(/([A-Z])/g, '_$1').toLowerCase();
}

export function objectKeysToLine(obj: Record<string, unknown> = {}): Record<string, unknown> {
  const keys = Object.keys(obj);
  const res = {};
  keys.forEach(v => (res[toLine(v)] = obj[v]));
  return res;
}

export function objectKeysToHump(obj: Record<string, unknown> = {}) {
  const keys = Object.keys(obj);
  const res = {};
  keys.forEach(v => (res[toHump(v)] = obj[v]));
  return res;
}

export function isKeyBoardEnter(e: React.KeyboardEvent) {
  if (e.keyCode === 13) {
    return true;
  }
  return false;
}

export function fetchCurrentMeegoCookie() {
  const cookies = document.cookie.split(';');
  const token = (cookies.find(item => /^\s*mission_token/.test(item)) || '')
    .replace('mission_token=', '')
    .replace(' ', '')
    .replace('%20', '');
  return token;
}

export function generateRequestHeaders() {
  const headers = {
    'x-meego-source': commonSourceParam,
    authorization: '',
  };
  const authorization = fetchCurrentMeegoCookie();
  if (authorization) {
    headers.authorization = authorization;
  }
  const csrfToken = Cookies.get('meego_csrf_token');
  if (csrfToken) {
    headers['x-meego-csrf-token'] = csrfToken;
  }
  return headers;
}

// libra qr image url
export function joinLibraQRCodeUrl(version_id: string) {
  return `https://rock.bytedance.net/ezconfig-ab/?version_id=${version_id}`;
}

declare global {
  interface Window {
    __test_scrollbar_width?: number;
  }
}

export function getScrollbarWidth() {
  if (window.__test_scrollbar_width === undefined) {
    // Creating invisible container
    const outer = document.createElement('div');
    outer.style.visibility = 'hidden';
    outer.style.overflow = 'scroll'; // forcing scrollbar to appear
    // outer.style.msOverflowStyle = 'scrollbar'; // needed for WinJS apps
    document.body.appendChild(outer);

    // Creating inner element and placing it in the container
    const inner = document.createElement('div');
    outer.appendChild(inner);

    // Calculating difference between container's full width and the child width
    window.__test_scrollbar_width = outer.offsetWidth - inner.offsetWidth;

    // Removing temporary elements from the DOM
    outer.parentNode!.removeChild(outer);
    return window.__test_scrollbar_width;
  }
  return window.__test_scrollbar_width;
}

export function openUrl(url: string) {
  if (!checkUrlValidity(url) || !url) {
    return;
  }
  window.open(url, '_blank');
}

/**
 * 改变 class 中函数的 this 指针
 * @description 修饰器函数一共可以接受三个参数，第一个参数是所要修饰的目标对象，第二个参数是所要修饰的属性名，第三个参数是该属性的描述对象
 * @param _target 所要修饰的目标对象
 * @param propertyKey 所要修饰的属性名
 * @param descriptor 该属性的描述对象
 */
// eslint-disable-next-line @typescript-eslint/ban-types
export function bound<T extends Function, P>(
  _target: P,
  propertyKey: string,
  descriptor: TypedPropertyDescriptor<T>,
): TypedPropertyDescriptor<T> {
  if (!descriptor || typeof descriptor.value !== 'function') {
    throw new TypeError(
      `Only methods can be decorated with @bind. <${propertyKey}> is not a method!`,
    );
  }
  return {
    configurable: true,
    get(this: T): T {
      const newTarget: T = descriptor.value!.bind(this);
      // Credits to https://github.com/andreypopp/autobind-decorator for memoizing the result of bind against a symbol on the instance.
      Object.defineProperty(this, propertyKey, {
        value: newTarget,
        configurable: true,
        writable: true,
      });
      return newTarget;
    },
  };
}

/**
 * @description 辅助ts中枚举类型的定义
 * https://stackoverflow.com/questions/46176165/ways-to-get-string-literal-type-of-array-values-without-enum-overhead
 * @param args <string | number>[]
 */
export const tuple = function <T extends string[]>(...args: T) {
  return args;
};

// copy from array-move
export const arrayMoveMutate = <T>(array: Array<T>, from: number, to: number) => {
  array.splice(to < 0 ? array.length + to : to, 0, array.splice(from, 1)[0]);
};

export const arrayMove = <T>(array: Array<T>, from: number, to: number) => {
  const tempArr = array.slice();
  arrayMoveMutate(tempArr, from, to);
  return tempArr;
};

export const arrayReplace = <T>(array: Array<T>, index: number, newItem: any) => {
  const tempArr = array.slice();
  tempArr[index] = newItem;
  return tempArr;
};

export const isUndefinedIfCastToString = (value: number) => `${value}` === 'undefined';

export function checkUrlValidity(url: string) {
  const legalUrlExp = new RegExp(
    // eslint-disable-next-line no-useless-escape
    /^http(s)?:\/\/([\w-]+\.)+[\w-]+(\/[\w- .\/?%&=]*)?/,
  );
  return legalUrlExp.test(url);
}

/**
 * 复制提醒
 * @parmas: content: string 复制内容
 * @parmas: massege: string toast提示内容 可选
 */
export function copyTips(content: string) {
  const el = document.createElement('textarea');
  el.value = content;
  el.setAttribute('readonly', '');
  el.setAttribute('style', "position: 'absolute', left: '-9999px'");
  document.body.appendChild(el);
  el.select();
  document.execCommand('copy');
  document.body.removeChild(el);
}

export const findFloorIndex = (positions: number[], current: number) => {
  let startIndex = 0;
  let endIndex = positions.length;
  while (endIndex - startIndex > 1) {
    const flagIndex = Math.floor(startIndex + (endIndex - startIndex) / 2);
    if (current < positions[flagIndex]) {
      endIndex = flagIndex;
    } else {
      startIndex = flagIndex;
    }
  }
  return startIndex;
};

export function makeEllipses(content: string, maxLength = 20) {
  return content.length > maxLength ? `${content.slice(0, maxLength - 1)}…` : content;
}

// 调试用的函数，暂时注释掉
// const memoryMap = new WeakMap();
//
// /**
//  * 用来模拟展示某个对象的「内存地址」:-)
//  *
//  * 当然，js 是无法获取到对象的真实内存地址的，但很多时候我们需要知道某个对象是否还是同一个（来判断是否触发 useEffect 等逻辑）
//  * 因此利用一个 WeakMap 保证同一个对象输出相同的虚拟内容（假装是内存地址），可以用于调试时比较
//  */
// export function memoryAddress(obj?: Record<string, unknown> | null) {
//   if (!obj) {
//     return '@0';
//   }
//   if (!memoryMap.has(obj)) {
//     memoryMap.set(
//       obj,
//       Math.floor(Math.random() * 1e9)
//         .toString(16)
//         .toUpperCase(),
//     );
//   }
//   return `@${memoryMap.get(obj)}`;
// }

export function getScrollContainer(node: Element | null): Element | null {
  if (!node) {
    return null;
  }
  const parent = node.parentElement;
  if (parent && parent.clientHeight < parent.scrollHeight) {
    return parent;
  } else {
    return getScrollContainer(parent);
  }
}

export function getUrlPath(url: string) {
  return url.includes('?') ? url.slice(0, url.indexOf('?') + 1) : url;
}

export function getUrlParams(url: string, encode = false) {
  let params = url.includes('?') ? url.slice(url.indexOf('?') + 1) : '';
  if (encode) {
    params = params
      .split('&')
      .map(queryItem => {
        try {
          const [key, value] = queryItem.split('=');
          if (key && value) {
            const deCodeValue = decodeURIComponent(value);
            return `${key}=${encodeURIComponent(deCodeValue)}`;
          }
          return queryItem;
        } catch {
          return queryItem;
        }
      })
      .join('&');
  }
  return params;
}

// export function formatItems(items: string[]): string {
//   const normalGlue = localTranslate('、');
//   const endingGlue = localTranslate('和'); // T_IGNORE
//   return (
//     items
//       .slice(0, -2)
//       .map(item => item + normalGlue)
//       .join('') + items.slice(-2).join(endingGlue)
//   );
// }

/** @deprecated 未使用 */
export const getElementPathInfo = (element: Element, deep?: number) => {
  let ele: Element | null = element;
  const pathInfo = [];
  while (ele && ele.tagName !== 'BODY' && (deep ? pathInfo.length < deep : true)) {
    pathInfo.push({
      tagName: ele.tagName,
      className: ele.className,
    });
    ele = ele.parentElement;
  }
  return pathInfo;
};

export { copyToClipboard } from './copyToClipboard';

export function isPromise<T, S = T>(obj: PromiseLike<T> | S): obj is PromiseLike<T> {
  return (
    Boolean(obj) &&
    (typeof obj === 'object' || typeof obj === 'function') &&
    typeof (obj as { then?: unknown }).then === 'function'
  );
}

export function isCurrentDomainRequest(url: string) {
  return url.startsWith(window?.location?.origin);
}

// 将字符串转回正则表达式
export function stringToRegex(regexString: string) {
  // 提取正则表达式的主体和标志
  const match = regexString.match(/\/(.+)\/([gimuy]*)$/);
  if (match) {
    const [, pattern, flags] = match;
    return new RegExp(pattern, flags);
  }
  throw new Error('Invalid regex string');
}
